import { DotLegalDialog, DotLegalRadioButton, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { Collapse, RadioGroup } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Box } from "@mui/system";
import DotLegalFileUploadWrapper from "../../../common/components/dotLegalFileUpload/DotLegalFileUploadWrapper";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import { useTranslation } from "../../../localization/useTranslation";
import { useAgreementDialogHooks } from "./DataProcessorAgreementDialog.hooks";
import { DataProcessorAgreementSaveModel } from "./DataProcessorAgreementDialog.types";
import { StepTypeEnum } from "../../ProcessingActivity.types";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import GroupEntitySelector from "@common/components/GroupEntitySelector";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";
import { DocumentTypeUsageType } from "../../../masterData/documentType/documentTypes/DocumentTypes.types";

export interface IAgreementDialog {
    onDialogClose: () => void;
    onSave: (agreementId: string) => void;
    saveModel: DataProcessorAgreementSaveModel;
    stepType: StepTypeEnum;
    usageType: DocumentTypeUsageType;
}

function AgreementDialog(props: IAgreementDialog) {
    const { translateString } = useTranslation();
    const {
        inProgress,
        viewModel,
        documentTypes,
        documentStatus,
        setViewModel,
        documentationOption,
        onDocumentationTypeChange,
        validation,
        createAgreement,
        onGroupEntityAccessIdsChange,
        onAllGroupEntitiesChange,
        gdpo,
        documentTypesIsLoading,
        uploadPercentage,
        onFilesChanged,
    } = useAgreementDialogHooks(props);

    const { permissions } = useUserContext();

    validation?.check(viewModel);
    return (
        <DotLegalDialog
            header={translateString("createAgreement")}
            size="md"
            inProgress={inProgress}
            buttonOkText={translateString("save")}
            open
            onDialogClose={() => props.onDialogClose()}
            onOkClick={() => createAgreement()}
            disableBackdropOnCloseIfValueChange={viewModel}
        >
            <Grid container columnSpacing={2}>
                <Grid size={{ md: 12 }}>
                    <DotLegalTextField
                        label={translateString("name")}
                        value={viewModel?.name ?? ""}
                        debounce={false}
                        onChange={(text) => {
                            let tempModel = { ...viewModel! };
                            tempModel.name = text;
                            setViewModel(tempModel);
                        }}
                        errorText={validation?.getErrorsForDisplay("name")}
                    />
                </Grid>
                <Grid size={{ md: 12 }}>
                    <DotLegalSelect
                        selectedItem={viewModel?.documentTypeId ? viewModel?.documentTypeId.toString() : undefined}
                        options={documentTypes}
                        placeholder={translateString("type")}
                        label={translateString("type")}
                        disableClearable
                        onChange={(documentType) => {
                            let tempModel = { ...viewModel! };
                            tempModel.documentTypeId = documentType ?? "";
                            setViewModel(tempModel);
                        }}
                        errorText={validation?.getErrorsForDisplay("documentTypeId")}
                        noOptionsLabel={translateString("noOptions")}
                        isLoading={documentTypesIsLoading}
                    />
                </Grid>

                <Grid size={{ md: 12 }}>
                    <DotLegalSelect
                        selectedItem={viewModel?.status ? viewModel?.status.toString() : undefined}
                        disabled={!permissions.canSetDocumentStatus}
                        options={documentStatus}
                        placeholder={translateString("status")}
                        label={translateString("status")}
                        disableClearable
                        errorText={validation?.getErrorsForDisplay("status")}
                        noOptionsLabel={translateString("noOptions")}
                        onChange={(status) => {
                            let tempModel = { ...viewModel! };
                            tempModel.status = Number(status);
                            setViewModel(tempModel);
                        }}
                    />
                </Grid>
            </Grid>
            <Box marginTop={2}>
                <RadioGroup
                    row
                    value={documentationOption}
                    onChange={(e) => {
                        onDocumentationTypeChange(Number((e.target as HTMLInputElement).value));
                    }}
                >
                    <DotLegalRadioButton value={1} labelPlacement="end" label={translateString("uploadDocument")} />
                    <DotLegalRadioButton value={0} labelPlacement="end" label={translateString("insertLink")} />
                </RadioGroup>

                <Collapse in={documentationOption === 1}>
                    <DotLegalFileUploadWrapper
                        errorText={validation?.getErrorsForDisplay("fileName")}
                        files={!isNullOrWhitespace(viewModel?.fileName) ? [viewModel?.fileName!] : []}
                        onFilesChanged={onFilesChanged}
                        pasteEnabled={documentationOption === 1}
                    />
                </Collapse>
                <Collapse in={documentationOption === 0}>
                    <Box sx={{ height: 70, "& > *": { marginTop: 0 } }}>
                        <DotLegalTextField
                            label={translateString("insertLink")}
                            value={viewModel?.link ?? ""}
                            debounce={false}
                            errorText={validation?.getErrorsForDisplay("link")}
                            onChange={(value) => {
                                let tempDoc = { ...viewModel! };
                                tempDoc.link = value;
                                setViewModel(tempDoc);
                            }}
                        ></DotLegalTextField>
                    </Box>
                </Collapse>
                <UploadProgressBar isUploading={inProgress && documentationOption === 1} uploadPercentage={uploadPercentage} />
            </Box>
            {!props.saveModel.isGroupEntity && (
                <Box mt={3}>
                    <GroupEntitySelector
                        switchLabel={translateString("globalDocument")}
                        explanation={translateString("globalDocumentTooltip")}
                        selectedUserGroupCompanies={viewModel?.groupEntityAccessIds?.map((id) => ({ userId: null, groupCompanyId: id }))}
                        onGroupCompaniesChange={onGroupEntityAccessIdsChange}
                        appliesToAllGroupEntities={viewModel?.allGroupEntities ?? false}
                        onAppliesToAllGroupEntitiesChange={onAllGroupEntitiesChange}
                        toggleDisabled={!gdpo}
                        visibleToNonGdpo
                        autoSelectSingleGroupEntity
                        showError={!!validation?.getErrorsForDisplay("groupEntityAccessIds")}
                        errorText={validation?.getErrorsForDisplay("groupEntityAccessIds")}
                    />
                </Box>
            )}
        </DotLegalDialog>
    );
}

export default AgreementDialog;
